import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import {
  useRunDetail,
} from "@tubo/api-models/hooks/run-api.hooks";
import { RunStatusEnum } from "@tubo/api-models/business/platform/platform.enums";
import { Button } from "@tubo/ui/components/ui/button";
import { RunReviewPage } from "@client/components/run-review/run-review-page";
import { isTerminalRunStatus } from "@client/components/semantic-platform/run/run-status";

/**
 * 抽取审核工作台（V2 抽取 T2.9 第三切片，design/25 §八 D「路由家族收编」）：
 * 整体迁入 data-center.review 路由家族（/data-center/review/run/$runId），
 * 与抽取任务审核（/data-center/review/$taskId）同族；根层级路由（不经过 _app shell），
 * 无侧边栏。全站只保留本路由（血缘、任务详情均直达此处），旧的重定向壳已下线。
 * 仅「待预检」(可预检写/局部重跑/完成审核)与「已完成」(只读回看)可进入。
 */
export const Route = createFileRoute("/data-center/review/run/$runId")({
  component: RunReviewRoute,
});

function RunReviewRoute() {
  const { runId } = Route.useParams();
  const navigate = useNavigate();

  const {
    data: run,
    isLoading,
    error,
    refetch,
  } = useRunDetail(runId, {
    query: {
      refetchInterval: (query) =>
        isTerminalRunStatus(query.state.data?.status) ? false : 5000,
      // 偶发网络/服务重启窗口导致的失败自动重试,避免误报"运行不存在"
      retry: 3,
      retryDelay: 1000,
    },
  });

  const enterable =
    run?.status === RunStatusEnum.Precheck ||
    run?.status === RunStatusEnum.Completed;

  // 不可进入的状态关掉本页(独立标签页,close 即回到来源页)
  useEffect(() => {
    if (run && !enterable) {
      window.close();
    }
  }, [run, enterable]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        加载中…
      </div>
    );
  }

  if (!run) {
    const notFound =
      isAxiosError(error) && error.response?.status === 404;
    return (
      <div className="flex h-screen flex-col items-center justify-center text-muted-foreground">
        <p className="text-sm">
          {notFound
            ? "运行不存在或已被删除"
            : "运行信息加载失败,请重试"}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() =>
            notFound
              ? void navigate({ to: "/semantic-platform" })
              : void refetch()
          }
        >
          {notFound ? "返回知识本体" : "重试"}
        </Button>
      </div>
    );
  }

  if (!enterable) {
    return (
      <div className="flex h-screen items-center justify-center text-muted-foreground">
        当前状态不支持审核,本页即将关闭…
      </div>
    );
  }

  return <RunReviewPage runId={runId} run={run} />;
}
